import stringSimilarity from "string-similarity";
import { fetchWithAuth } from "../utils/fetchWithAuth";

class StrategyWorkflowService {
  constructor() {
    this.agentProfiles = null; // Will be loaded from API
    this.STRATEGY_STEPS = [
      {
        id: "analyze",
        title: "Analyzing your brief",
        description: "Understanding your design challenge and requirements",
        status: "pending",
        agentKey: "DESIGN_LEAD",
      },
      {
        id: "objectives",
        title: "Setting objectives",
        description: "Defining clear goals and success metrics",
        status: "pending",
        agentKey: "DESIGN_LEAD",
      },
      {
        id: "programs",
        title: "Selecting approach",
        description:
          "Choosing the best design programs, design tools and methods",
        status: "pending",
        agentKey: "UX_RESEARCH",
      },
      {
        id: "timeline",
        title: "Creating roadmap",
        description: "Building your execution program of work",
        status: "pending",
        agentKey: "PROJECT_MANAGER",
      },
      {
        id: "finalize",
        title: "Finalizing strategy",
        description: "Preparing your complete design strategy",
        status: "pending",
        agentKey: "DESIGN_LEAD",
      },
    ];

    this.workflowResults = {};
    this.conversationHistory = [];
  }

  // Helper functions from original generateActivityPlan.js
  preprocessText(text) {
    if (Array.isArray(text)) {
      text = text.join(" ");
    }

    if (typeof text !== "string") {
      console.log("preprocessText: Expected a string, but got:", text);
      return [];
    }

    const stopWords = [
      "and",
      "or",
      "the",
      "is",
      "to",
      "for",
      "in",
      "on",
      "with",
      "as",
    ];
    const words = text.toLowerCase().split(/\s+/);
    const filteredWords = words.filter(
      (word) => !stopWords.includes(word) && word.length > 1,
    );
    return filteredWords;
  }

  mapComplexityToScore(complexity) {
    const complexityMap = {
      simple: 1,
      medium: 2,
      complex: 3,
    };
    return complexityMap[complexity.toLowerCase()] || 1;
  }

  detectComplexity(designChallenge) {
    const complexityKeywords = {
      simple: 1,
      basic: 1,
      straightforward: 1,
      moderate: 2,
      intermediate: 2,
      challenging: 3,
      complex: 3,
      complicated: 3,
      "multi-phased": 3,
      advanced: 3,
    };

    for (const [keyword, multiplier] of Object.entries(complexityKeywords)) {
      if (designChallenge.toLowerCase().includes(keyword.toLowerCase())) {
        return multiplier;
      }
    }

    return 2.0; // Default complexity
  }

  detectUrgency(designChallenge) {
    const urgencyKeywords = {
      "1 week": 1,
      "2 weeks": 1,
      quickly: 1,
      weeks: 2,
      months: 3,
      asap: 1,
      immediate: 2,
      minor: 1,
      urgent: 1,
      quick: 1,
      fast: 2,
      deadline: 2,
      rapid: 1,
      rapidly: 1,
    };

    for (const [keyword, multiplier] of Object.entries(urgencyKeywords)) {
      if (designChallenge.toLowerCase().includes(keyword.toLowerCase())) {
        return multiplier;
      }
    }

    return 1.0; // Default urgency
  }

  determineToolDuration(tool, complexityScore, urgencyMultiplier) {
    if (!tool) return 1;

    let lowDuration = parseFloat(tool.low_duration) || 1;
    let highDuration = parseFloat(tool.high_duration) || lowDuration;

    let adjustedDuration = highDuration;

    if (urgencyMultiplier > 1) {
      adjustedDuration /= urgencyMultiplier;
    }

    let complexityAdjustment = (highDuration - lowDuration) * complexityScore;
    adjustedDuration -= complexityAdjustment;

    if (adjustedDuration > highDuration) {
      adjustedDuration = highDuration;
    } else if (adjustedDuration < lowDuration) {
      adjustedDuration = lowDuration;
    }

    return Math.round(adjustedDuration);
  }

  filterRelevantTools(tools, designChallenge, stagesArray, programMethods) {
    const normalizedStages = stagesArray.map((stage) => stage.toLowerCase());
    const normalizedProgramMethods = programMethods.map((method) =>
      method.toLowerCase(),
    );

    const toolsWithScores = tools.map((tool) => {
      const toolStage = tool.related_stage
        ? tool.related_stage.toLowerCase()
        : "";
      const stagePass = normalizedStages.includes(toolStage);

      const toolDescription = tool.description
        ? tool.description.toLowerCase()
        : "";
      const descriptionMatchScore = stringSimilarity.compareTwoStrings(
        designChallenge.toLowerCase(),
        toolDescription,
      );

      const toolName = tool.tool ? tool.tool.toLowerCase() : "";
      const nameMatchScore = stringSimilarity.compareTwoStrings(
        designChallenge.toLowerCase(),
        toolName,
      );

      const complexityMatch =
        this.detectComplexity(designChallenge) >=
        this.mapComplexityToScore(tool.complexity || "simple");

      const toolMethods = tool.related_methods
        ? tool.related_methods
            .split(",")
            .map((method) => method.trim().toLowerCase())
        : [];
      const methodMatch = toolMethods.some((toolMethod) =>
        normalizedProgramMethods.includes(toolMethod),
      );

      const similarityScore = tool.similarity_score || 0;

      const stageWeight = 2.0;
      const descriptionWeight = 0.5;
      const nameWeight = 0.5;
      const complexityWeight = 0.3;
      const methodWeight = 1.0;
      const similarityWeight = 3.0;

      let totalScore = 0;
      if (stagePass) totalScore += stageWeight;
      totalScore += descriptionMatchScore * descriptionWeight;
      totalScore += nameMatchScore * nameWeight;
      if (complexityMatch) totalScore += complexityWeight;
      if (methodMatch) totalScore += methodWeight;
      totalScore += similarityScore * similarityWeight;

      if (!methodMatch) {
        totalScore = 0;
      }

      return {
        ...tool,
        totalScore,
      };
    });

    toolsWithScores.sort((a, b) => b.totalScore - a.totalScore);

    const topScore =
      toolsWithScores.length > 0 ? toolsWithScores[0].totalScore : 0;
    const percentageGap = 0.07;
    const threshold = topScore * (1 - percentageGap);

    const topTools = toolsWithScores.filter(
      (tool) => tool.totalScore >= threshold,
    );

    return topTools;
  }

  filterRelevantDeliverables(deliverables, designChallenge, programOfWork) {
    const preprocessedChallenge = this.preprocessText(designChallenge);

    return deliverables.filter((deliverable) => {
      let isRelevantToChallenge = false;
      let isRelevantToProgram =
        deliverable.related_program_of_work.includes(programOfWork);

      const deliverableDescription = deliverable.description
        ? deliverable.description.toLowerCase()
        : "";
      const deliverableTitle = deliverable.deliverable
        ? deliverable.deliverable.toLowerCase()
        : "";

      preprocessedChallenge.forEach((keyword) => {
        if (
          deliverableDescription.includes(keyword) ||
          deliverableTitle.includes(keyword)
        ) {
          isRelevantToChallenge = true;
        }
      });

      return isRelevantToChallenge && isRelevantToProgram;
    });
  }

  fuzzyKeywordMatch(programKeywords, lemmatizedChallenge) {
    // Use the pre-lemmatized challenge from the backend
    const challengeWords = Array.isArray(lemmatizedChallenge)
      ? lemmatizedChallenge
      : this.preprocessText(lemmatizedChallenge || "");

    const programKeywordList = programKeywords
      ? programKeywords
          .toLowerCase()
          .split(",")
          .flatMap((keyword) => keyword.trim().split(" "))
      : [];

    const fuzzyMatches = programKeywordList
      .map((keyword) => {
        const matchScore = challengeWords.some(
          (word) =>
            stringSimilarity.compareTwoStrings(word, keyword.trim()) > 0.6,
        );
        return matchScore ? 1 : 0;
      })
      .reduce((acc, curr) => acc + curr, 0);

    const logKeywordMatch = Math.log(1 + fuzzyMatches);

    return logKeywordMatch;
  }

  calculateProgramLengthAndStages(relevantTools) {
    const stages = {};

    relevantTools.forEach((tool) => {
      const stageName = tool.related_stage || "Default Stage";
      if (!stages[stageName]) {
        stages[stageName] = { tools: [], totalDuration: 0 };
      }
      stages[stageName].tools.push(tool);
      stages[stageName].totalDuration += parseFloat(tool.duration) || 0;
    });

    return stages;
  }

  getSteps() {
    return [...this.STRATEGY_STEPS];
  }

  async executeWorkflow(
    designChallenge,
    programsOfWork,
    methods,
    tools,
    deliverables,
    callbacks,
  ) {
    const { onStepStart, onStepComplete, onComplete, onError, onChatMessage } =
      callbacks;

    try {
      const steps = this.getSteps();

      // Step 1: Analyze - Dynamic Agent
      const analyzeAgent = this.getAgentForStep("analyze");
      const analyzeAgentName = analyzeAgent ? analyzeAgent.name : "Product Lead";
      if (onStepStart) onStepStart(0, { ...steps[0], agent: analyzeAgentName });

      // Note: Removed redundant agent message - workflow UI shows progress
      const analyzeResults = await this.analyzeStep(designChallenge);
      console.log("📝 Step 1 (Analyze) completed by Product Lead");
      if (onStepComplete)
        onStepComplete(
          0,
          { ...steps[0], status: "completed", agent: "Product Lead" },
          analyzeResults,
        );

      // Step 2: Objectives - Dynamic Agent
      const objectivesAgent = this.getAgentForStep("objectives");
      const objectivesAgentName = objectivesAgent
        ? objectivesAgent.name
        : "Product Lead";
      if (onStepStart)
        onStepStart(1, { ...steps[1], agent: objectivesAgentName });

      // Note: Removed redundant agent message - workflow UI shows progress
      const objectivesResults = await this.objectivesStep(designChallenge);
      console.log("📝 Step 2 (Objectives) completed by Product Lead");
      if (onStepComplete)
        onStepComplete(
          1,
          { ...steps[1], status: "completed", agent: "Product Lead" },
          objectivesResults,
        );

      // Step 3: Programs - Dynamic Agent
      const programsAgent = this.getAgentForStep("programs");
      const programsAgentName = programsAgent
        ? programsAgent.name
        : "UX Research Lead";
      if (onStepStart)
        onStepStart(2, { ...steps[2], agent: programsAgentName });

      // Note: Removed redundant agent message - workflow UI shows progress
      const programsResults = await this.programsStep(
        designChallenge,
        programsOfWork,
        methods,
        tools,
        deliverables,
      );
      console.log(`📝 Step 3 (Programs) completed by ${programsAgentName}`);
      if (onStepComplete)
        onStepComplete(
          2,
          { ...steps[2], status: "completed", agent: programsAgentName },
          programsResults,
        );

      // Step 4: Timeline - Dynamic Agent
      const timelineAgent = this.getAgentForStep("timeline");
      const timelineAgentName = timelineAgent
        ? timelineAgent.name
        : "Project Manager";
      if (onStepStart)
        onStepStart(3, { ...steps[3], agent: timelineAgentName });

      // Note: Removed redundant agent message - workflow UI shows progress
      const timelineResults = await this.timelineStep();
      console.log(`📝 Step 4 (Timeline) completed by ${timelineAgentName}`);
      if (onStepComplete)
        onStepComplete(
          3,
          { ...steps[3], status: "completed", agent: timelineAgentName },
          timelineResults,
        );

      // Step 5: Finalize - Dynamic Agent
      const finalizeAgent = this.getAgentForStep("finalize");
      const finalizeAgentName = finalizeAgent
        ? finalizeAgent.name
        : "UX Design Lead";
      if (onStepStart)
        onStepStart(4, { ...steps[4], agent: finalizeAgentName });

      // Note: Removed redundant agent message - workflow UI shows progress
      const finalizeResults = await this.finalizeStep();
      console.log(`📝 Step 5 (Finalize) completed by ${finalizeAgentName}`);
      if (onStepComplete)
        onStepComplete(
          4,
          { ...steps[4], status: "completed", agent: finalizeAgentName },
          finalizeResults,
        );

      const compiledResults = this.compileWorkflowResults();

      if (onComplete) {
        onComplete(compiledResults);
      }

      return compiledResults;
    } catch (error) {
      console.error("Workflow execution failed:", error);
      if (onError) {
        onError(error);
      }
      throw error;
    }
  }

  async analyzeStep(designChallenge) {
    try {
      await this.loadAgentProfiles();
      const agent = this.getAgentForStep("analyze");

      // Check if agent actually has the required tool
      const requiredTool = "analyze_brief";
      const canExecute =
        agent &&
        agent.primary_ai_capabilities &&
        agent.primary_ai_capabilities.includes(requiredTool);

      console.log(
        "🔍 StrategyWorkflowService.analyzeStep - Dynamic agent tool execution:",
        {
          step: "analyze",
          agentId: agent ? agent.id : null,
          agentName: agent ? agent.name : "No Agent",
          requiredTool,
          canExecute,
          designChallenge: designChallenge.substring(0, 100) + "...",
        },
      );

      // CONSOLIDATED: Use autonomous-analysis endpoint for robust strategy generation
      // This ensures Decision Call and Full Strategy use the same high-quality analysis prompts
      console.log(`🔧 Executing ${requiredTool} via autonomous-analysis endpoint (consolidated)`);
      const result = await fetchWithAuth("/autonomous-ai/autonomous-analysis", {
        method: "POST",
        body: {
          design_challenge: designChallenge,
          agentId: agent ? this.getAgentStringId(agent.id) : "DESIGN_LEAD",
          analysis_type: "deep_contextual",
          use_csv_as_knowledge: false,
        },
      });

      if (!result.ok) {
        throw new Error(`Analysis API failed with status ${result.status}`);
      }

      const resultData = await result.json();

      // CRITICAL FIX: Ensure proper data structure and validate required fields
      if (!resultData || typeof resultData !== "object") {
        console.error("❌ Analyze step returned invalid result:", resultData);
        throw new Error("Analysis failed - invalid response from AI");
      }

      console.log("🔍 Autonomous analysis response received:", {
        hasShortSummary: !!resultData.short_summary,
        hasProblemStatement: !!resultData.problem_statement,
        hasStakeholders: !!resultData.stakeholders_involvement,
        hasOpportunityAreas: !!resultData.opportunity_areas,
        responseKeys: Object.keys(resultData),
      });

      // Validate and normalize the analysis result (handles both old and new response formats)
      const normalizedResult = {
        short_summary:
          resultData.short_summary ||
          resultData.shortSummary ||
          designChallenge.substring(0, 100) + "...",
        problem_statement:
          resultData.problem_statement ||
          resultData.problemStatement ||
          "Problem statement to be defined",
        goal_statement:
          resultData.goal_statement ||
          resultData.goalStatement ||
          "Strategic goals to be defined",
        target_statement:
          resultData.target_statement ||
          resultData.targetStatement ||
          "Target audience to be defined",
        objectives_statement: Array.isArray(resultData.objectives_statement)
          ? resultData.objectives_statement
          : Array.isArray(resultData.objectivesStatement)
            ? resultData.objectivesStatement
            : resultData.objectives_statement
              ? [resultData.objectives_statement]
              : ["Key objective 1", "Key objective 2"],
        deliverables_statement: Array.isArray(resultData.deliverables_statement)
          ? resultData.deliverables_statement
          : Array.isArray(resultData.deliverablesStatement)
            ? resultData.deliverablesStatement
            : resultData.deliverables_statement
              ? [resultData.deliverables_statement]
              : ["Key deliverable 1", "Key deliverable 2"],
        metrics_statement: Array.isArray(resultData.metrics_statement)
          ? resultData.metrics_statement
          : Array.isArray(resultData.metricsStatement)
            ? resultData.metricsStatement
            : resultData.metrics_statement
              ? [resultData.metrics_statement]
              : ["Success metric 1", "Success metric 2"],
        experience_design_principles:
          resultData.experience_design_principles ||
          resultData.experienceDesignPrinciples ||
          "Experience design principles to be defined",
        // Additional fields from autonomous-analysis (enhanced strategy content)
        stakeholders_involvement: Array.isArray(resultData.stakeholders_involvement)
          ? resultData.stakeholders_involvement
          : resultData.stakeholders_involvement
            ? [resultData.stakeholders_involvement]
            : [],
        contextual_factors: Array.isArray(resultData.contextual_factors)
          ? resultData.contextual_factors
          : [],
        domain_insights: Array.isArray(resultData.domain_insights)
          ? resultData.domain_insights
          : [],
        unique_constraints: Array.isArray(resultData.unique_constraints)
          ? resultData.unique_constraints
          : [],
        opportunity_areas: Array.isArray(resultData.opportunity_areas)
          ? resultData.opportunity_areas
          : [],
      };

      this.workflowResults.analyze = normalizedResult;

      console.log("✅ Agent-driven analyze step completed and normalized:", {
        hasShortSummary: !!normalizedResult.short_summary,
        hasProblemStatement: !!normalizedResult.problem_statement,
        hasGoalStatement: !!normalizedResult.goal_statement,
        hasTargetStatement: !!normalizedResult.target_statement,
        hasObjectives:
          Array.isArray(normalizedResult.objectives_statement) &&
          normalizedResult.objectives_statement.length > 0,
        hasDeliverables:
          Array.isArray(normalizedResult.deliverables_statement) &&
          normalizedResult.deliverables_statement.length > 0,
        hasMetrics:
          Array.isArray(normalizedResult.metrics_statement) &&
          normalizedResult.metrics_statement.length > 0,
        resultPreview: {
          shortSummary: normalizedResult.short_summary.substring(0, 50) + "...",
          problemStatement:
            normalizedResult.problem_statement.substring(0, 50) + "...",
          goalStatement:
            normalizedResult.goal_statement.substring(0, 50) + "...",
        },
      });

      return normalizedResult;
    } catch (error) {
      console.error("Analyze step failed:", error);
      throw error;
    }
  }

  async objectivesStep(designChallenge) {
    try {
      await this.loadAgentProfiles();
      const agent = this.getAgentForStep("objectives");

      // Check if agent actually has the required tool
      const requiredTool = "generate_goal";
      const canExecute =
        agent &&
        agent.primary_ai_capabilities &&
        agent.primary_ai_capabilities.includes(requiredTool);

      console.log(
        "🔍 StrategyWorkflowService.objectivesStep - Dynamic agent tool execution:",
        {
          step: "objectives",
          agentId: agent ? agent.id : null,
          agentName: agent ? agent.name : "No Agent",
          requiredTool,
          canExecute,
          designChallenge: designChallenge.substring(0, 100) + "...",
        },
      );

      // Use direct API call with fetchWithAuth (this is the primary method)
      console.log(`🔧 Executing ${requiredTool} via direct API with fetchWithAuth`);
      const response = await fetchWithAuth("/ai/generate-goal", {
        method: "POST",
        body: {
          design_challenge: designChallenge,
          agentId: agent ? this.getAgentStringId(agent.id) : "DESIGN_LEAD",
          context: { source: "strategy_workflow", step: "objectives" },
        },
      });

      if (!response.ok) {
        throw new Error(`Goals API failed with status ${response.status}`);
      }

      let result = await response.json();

      // CRITICAL FIX: Validate and normalize objectives result
      if (!result || typeof result !== "object") {
        console.error("❌ Objectives step returned invalid result:", result);
        result = {
          goal_statement: "Strategic goals to be defined",
          objectives: ["Key objective 1", "Key objective 2"],
        };
      }

      // Normalize the objectives result
      const normalizedResult = {
        goal_statement:
          result.goal_statement ||
          result.goalStatement ||
          "Strategic goals to be defined",
        objectives: Array.isArray(result.objectives)
          ? result.objectives
          : Array.isArray(result.objectives_statement)
            ? result.objectives_statement
            : result.objectives
              ? [result.objectives]
              : ["Key objective 1", "Key objective 2"],
      };

      this.workflowResults.objectives = normalizedResult;

      console.log("✅ Agent-driven objectives step completed and normalized:", {
        hasGoalStatement: !!normalizedResult.goal_statement,
        hasObjectives:
          Array.isArray(normalizedResult.objectives) &&
          normalizedResult.objectives.length > 0,
        goalPreview: normalizedResult.goal_statement.substring(0, 50) + "...",
        objectivesCount: normalizedResult.objectives.length,
      });

      return normalizedResult;
    } catch (error) {
      console.error("Objectives step failed:", error);
      throw error;
    }
  }

  async programsStep(
    designChallenge,
    programsOfWork,
    methods,
    tools,
    deliverables,
  ) {
    try {
      console.log("🔄 Programs step: Using sophisticated scoring system...");

      // IMMEDIATE: Show progress update before heavy processing
      console.log(
        "🔍 Starting similarity computation - this may take 15-20 seconds...",
      );

      // Get similarity scores from backend (THIS IS THE SLOW PART)
      const response = await fetchWithAuth(
        "/ai/compute-similarity-scores",
        {
          method: "POST",
          body: {
            design_challenge: designChallenge,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Similarity API failed with status ${response.status}`);
      }

      const similarityData = await response.json();
      console.log("✅ Programs step: Received similarity data:", {
        programs: similarityData.programs?.length || 0,
        methods: similarityData.methods?.length || 0,
        tools: similarityData.tools?.length || 0,
      });

      const lemmatizedChallenge = similarityData.lemmatized_challenge;

      // Load actual CSV data from the similarity response
      const actualPrograms = similarityData.programs || [];
      const actualTools = similarityData.tools || [];

      console.log("Using actual CSV data:", {
        programsCount: actualPrograms.length,
        toolsCount: actualTools.length,
      });

      // Map similarity scores to programs and tools using actual data
      const programsWithScores = actualPrograms.map((programData) => {
        return {
          ...programData,
          similarity_score: programData.similarity_score || 0,
        };
      });

      const toolsWithScores = actualTools.map((toolData) => {
        return {
          ...toolData,
          similarity_score: toolData.similarity_score || 0,
        };
      });

      // Apply sophisticated scoring system
      const semanticSimilarityWeight = 3.0;
      const programMatchWeight = 1.0;
      const keywordMatchWeight = 0.4;
      const focusMatchWeight = 0.6;
      const descriptionMatchWeight = 0.4;
      const outcomeMatchWeight = 0.5;

      const designChallengeLower = designChallenge.toLowerCase();

      // Calculate program scores with sophisticated scoring system
      const scoredPrograms = programsWithScores.map((program) => {
        // Skip 'Preparation' and 'Implementation' phases from scoring
        if (
          program.phase === "Preparation" ||
          program.phase === "Implementation"
        ) {
          return { ...program, score: 0 };
        }

        // Similarity scores using string similarity
        const programMatch = program?.program_of_work
          ? stringSimilarity.compareTwoStrings(
              designChallengeLower,
              program.program_of_work.toLowerCase(),
            )
          : 0;

        const descriptionMatch = program.description
          ? stringSimilarity.compareTwoStrings(
              designChallengeLower,
              program.description.toLowerCase(),
            )
          : 0;

        const outcomeMatch = program.outcome
          ? stringSimilarity.compareTwoStrings(
              designChallengeLower,
              program.outcome.toLowerCase(),
            )
          : 0;

        // Keyword and focus matches
        const keywordMatch = this.fuzzyKeywordMatch(
          program.keywords,
          lemmatizedChallenge,
        );
        const focusMatch = program.Focus
          ? program.Focus.toLowerCase()
              .split(",")
              .reduce(
                (acc, focus) =>
                  acc + (designChallengeLower.includes(focus.trim()) ? 1 : 0),
                0,
              )
          : 0;

        // Calculate total score using weighted system
        let score =
          program.similarity_score * semanticSimilarityWeight +
          programMatch * programMatchWeight +
          keywordMatch * keywordMatchWeight +
          focusMatch * focusMatchWeight +
          descriptionMatch * descriptionMatchWeight +
          outcomeMatch * outcomeMatchWeight;

        const urgencyMultiplier = this.detectUrgency(designChallenge);
        const complexityMultiplier = this.detectComplexity(designChallenge);

        score *= urgencyMultiplier * complexityMultiplier;

        // Debug logging for each program
        console.log(`\n--- Program Evaluation ---`);
        console.log(`Program: ${program.program_of_work}`);
        console.log(`Phase: ${program.phase}`);
        console.log(
          `Similarity Score: ${program.similarity_score?.toFixed(4)} (Weighted: ${(program.similarity_score * semanticSimilarityWeight).toFixed(4)})`,
        );
        console.log(
          `Program Match: ${programMatch.toFixed(4)} (Weighted: ${(programMatch * programMatchWeight).toFixed(4)})`,
        );
        console.log(
          `Keyword Match: ${keywordMatch.toFixed(4)} (Weighted: ${(keywordMatch * keywordMatchWeight).toFixed(4)})`,
        );
        console.log(
          `Focus Match: ${focusMatch} (Weighted: ${(focusMatch * focusMatchWeight).toFixed(4)})`,
        );
        console.log(
          `Description Match: ${descriptionMatch.toFixed(4)} (Weighted: ${(descriptionMatch * descriptionMatchWeight).toFixed(4)})`,
        );
        console.log(
          `Outcome Match: ${outcomeMatch.toFixed(4)} (Weighted: ${(outcomeMatch * outcomeMatchWeight).toFixed(4)})`,
        );
        console.log(`Urgency Multiplier: ${urgencyMultiplier}`);
        console.log(`Complexity Multiplier: ${complexityMultiplier}`);
        console.log(`Final Score: ${score.toFixed(4)}`);
        console.log(`---------------------------\n`);

        return {
          ...program,
          score,
        };
      });

      // Sort and select programs using threshold
      scoredPrograms.sort((a, b) => b.score - a.score);

      console.log("🔍 Raw similarity data programs:", {
        totalPrograms: scoredPrograms.length,
        programsWithScores: scoredPrograms.slice(0, 12).map((p) => ({
          name: p.program_of_work,
          score: parseFloat(p.score.toFixed(3)),
          phase: p.phase,
        })),
      });

      // STRICTER: Calculate weighted PROGRAM OF WORK selection with tighter thresholds
      const percentageGap = 0.08; // 8% gap from top score for much more selectivity
      const topScore = scoredPrograms.length > 0 ? scoredPrograms[0].score : 0;
      const scoreThreshold = topScore * (1 - percentageGap);

      console.log(
        `Top score: ${topScore.toFixed(4)}, STRICT Threshold (${percentageGap * 100}% gap): ${scoreThreshold.toFixed(4)}`,
      );

      // Apply STRICT threshold filtering
      let selectedPrograms = scoredPrograms.filter(
        (p) =>
          p.score >= scoreThreshold &&
          p.phase !== "Preparation" &&
          p.phase !== "Implementation",
      );

      console.log(
        `🎯 Programs above STRICT threshold (${scoreThreshold.toFixed(4)}):`,
        selectedPrograms.length,
      );

      // Fallback: if no programs meet threshold, select only the TOP program
      if (selectedPrograms.length === 0) {
        const highestScoringProgram = scoredPrograms.find(
          (p) => p.phase !== "Preparation" && p.phase !== "Implementation",
        );
        if (highestScoringProgram) {
          selectedPrograms = [highestScoringProgram];
          console.log(
            `🚨 No programs met STRICT threshold, using only highest scoring: ${highestScoringProgram.program_of_work} (${highestScoringProgram.score.toFixed(4)})`,
          );
        }
      }

      // STRICTER: Higher similarity requirement and limit to 2 programs max
      selectedPrograms = selectedPrograms.filter(
        (p) => p.similarity_score > 0.88,
      ); // Only extremely relevant programs

      // Limit to maximum 2 programs for highly focused selection
      if (selectedPrograms.length > 2) {
        selectedPrograms = selectedPrograms.slice(0, 2);
        console.log(`✂️ LIMITED to top 2 programs for focused approach`);
      }

      console.log("🔧 Final program selection:", {
        selectedCount: selectedPrograms.length,
        programs: selectedPrograms.map((p) => ({
          name: p.program_of_work,
          score: parseFloat(p.score.toFixed(3)),
          phase: p.phase,
        })),
      });

      // Build final program sequence with proper phase ordering - PREVENT DUPLICATION
      const finalProgramSequence = [];
      const addedProgramIds = new Set(); // Track added programs to prevent duplicates

      console.log(
        "🔧 Building program sequence from",
        selectedPrograms.length,
        "selected programs",
      );

      // Add preparation if available
      const preparationProgram = scoredPrograms.find(
        (p) => p.phase === "Preparation",
      );
      if (
        preparationProgram &&
        !addedProgramIds.has(preparationProgram.program_of_work)
      ) {
        finalProgramSequence.push(preparationProgram);
        addedProgramIds.add(preparationProgram.program_of_work);
      }

      // Ensure we have programs to work with
      if (selectedPrograms.length === 0) {
        console.log("⚠️ No selected programs found, using fallback approach");
        // Take top 3-5 programs regardless of score as emergency fallback
        const sortedPrograms = scoredPrograms
          .filter(
            (p) => p.phase !== "Preparation" && p.phase !== "Implementation",
          )
          .sort((a, b) => (b.score || 0) - (a.score || 0));
        selectedPrograms = sortedPrograms.slice(
          0,
          Math.min(5, sortedPrograms.length),
        );
        console.log(
          `🚨 Emergency fallback: selected ${selectedPrograms.length} programs`,
        );
      }

      let lastPhaseAdded = null;
      let designPhaseIndex = -1;

      const phases = [
        "Preparation",
        "Strategy",
        "Discovery",
        "Design",
        "Development",
        "Implementation",
      ];

      phases.forEach((phase) => {
        const programsInPhase = selectedPrograms.filter(
          (program) =>
            program.phase === phase &&
            !addedProgramIds.has(program.program_of_work),
        );
        if (programsInPhase.length > 0) {
          if (phase === "Discovery") {
            const desiredOrder = [
              "Insight Sprint",
              "Digital Discovery",
              "CX Sprint",
              "Design Sprint",
            ];
            programsInPhase.sort((a, b) => {
              const indexA = desiredOrder.indexOf(a.program_of_work);
              const indexB = desiredOrder.indexOf(b.program_of_work);
              return (
                (indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA) -
                (indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB)
              );
            });
          }
          programsInPhase.forEach((program) => {
            if (!addedProgramIds.has(program.program_of_work)) {
              finalProgramSequence.push(program);
              addedProgramIds.add(program.program_of_work);
              if (phase === "Design" && designPhaseIndex === -1) {
                designPhaseIndex = finalProgramSequence.length - 1;
              }
              lastPhaseAdded = phase;
            }
          });
        }
      });

      // Handle Implementation phase insertion - PREVENT DUPLICATION
      const implementationProgram = scoredPrograms.find(
        (p) => p.phase === "Implementation",
      );
      const hasDiscoveryOrStrategy = selectedPrograms.some(
        (p) => p.phase === "Discovery" || p.phase === "Strategy",
      );
      const hasDesign = selectedPrograms.some((p) => p.phase === "Design");

      if (
        implementationProgram &&
        !addedProgramIds.has(implementationProgram.program_of_work)
      ) {
        if (!hasDiscoveryOrStrategy && hasDesign) {const designIndex = finalProgramSequence.findIndex(
            (p) => p.phase === "Design",
          );
          if (designIndex !== -1) {
            finalProgramSequence.splice(
              designIndex + 1,
              0,
              implementationProgram,
            );
            addedProgramIds.add(implementationProgram.program_of_work);
          }
        } else {
          const insertIndex = lastPhaseAdded
            ? finalProgramSequence
                .map((p) => p.phase)
                .lastIndexOf(lastPhaseAdded) + 1
            : finalProgramSequence.length;

          finalProgramSequence.splice(insertIndex, 0, implementationProgram);
          addedProgramIds.add(implementationProgram.program_of_work);
        }
      }

      console.log(`✅ Selected programs:`, {
        count: selectedPrograms.length,
        programs: selectedPrograms.map((p) => ({
          name: p.program_of_work,
          score: p.score || 0,
          phase: p.phase,
        })),
      });

      // CRITICAL: Detect and prevent exponential duplication
      if (finalProgramSequence.length > 15) {
        console.error("❌ EXPONENTIAL DUPLICATION DETECTED:", {
          count: finalProgramSequence.length,
          programNames: finalProgramSequence
            .slice(0, 5)
            .map((p) => p.program_of_work || p.name),
        });
        throw new Error(
          `Exponential duplication detected: ${finalProgramSequence.length} programs. This is a bug in the workflow.`,
        );
      }

      // Check for duplicate program names (another duplication indicator)
      const programNames = finalProgramSequence.map(
        (p) => p.program_of_work || p.name || "unnamed",
      );
      const uniqueNames = new Set(programNames);
      if (programNames.length > uniqueNames.size * 2) {
        console.error("❌ DUPLICATE PROGRAMS DETECTED:", {
          total: programNames.length,
          unique: uniqueNames.size,
          duplicates: programNames.filter(
            (name, index) => programNames.indexOf(name) !== index,
          ),
        });
        throw new Error(
          "Duplicate programs detected in workflow. This indicates a corruption bug.",
        );
      }

      // Process each program with tools and deliverables
      finalProgramSequence.forEach((program) => {
        // Ensure program has required fields with safe defaults
        program.program_of_work =
          program.program_of_work || program.name || "Unknown Program";
        program.phase = program.phase || "Discovery";

        // Parse program fields safely
        if (typeof program.Stages === "string") {
          program.Stages = program.Stages.split(",").map((stage) =>
            stage.trim(),
          );
        } else if (!Array.isArray(program.Stages)) {
          // Dynamic default stages based on program type
          const programName = program.program_of_work?.toLowerCase() || "";
          if (programName.includes("sprint")) {
            program.Stages = ["Understand", "Define", "Ideate", "Prototype", "Test"];
          } else if (programName.includes("research")) {
            program.Stages = ["Planning", "Data Collection", "Analysis", "Insights"];
          } else if (programName.includes("design")) {
            program.Stages = ["Concept", "Design", "Iterate"];
          } else {
            program.Stages = ["Setup", "Execute", "Review"]; // Minimal default
          }
        }

        if (typeof program.related_methods === "string") {
          program.related_methods = program.related_methods
            .split(",")
            .map((method) => method.trim())
            .filter(method => method && method !== 'null' && method !== 'undefined');
        } else if (!Array.isArray(program.related_methods)) {
          program.related_methods = ["User Research", "Design Thinking"]; // Default methods
        } else {
          // Clean existing array of null strings
          program.related_methods = program.related_methods
            .filter(method => method && typeof method === 'string' && method !== 'null' && method !== 'undefined');
        }

        // Ensure we have at least one valid method
        if (!program.related_methods || program.related_methods.length === 0) {
          program.related_methods = ["User Research", "Design Thinking"];
        }

        if (typeof program.Focus === "string") {
          program.Focus = program.Focus.split(",").map((focus) => focus.trim());
        } else if (!Array.isArray(program.Focus)) {
          program.Focus = ["User Experience"]; // Default focus
        }

        // Filter relevant tools
        let relevantTools = this.filterRelevantTools(
          toolsWithScores,
          designChallenge,
          program.Stages,
          program.related_methods,
        );

      // Apply sequence awareness to filter tools based on next program
      const programIndex = finalProgramSequence.indexOf(program);
      const nextProgram = finalProgramSequence[programIndex + 1];

      if (nextProgram && relevantTools.length > 0) {
        // Filter out tools that would overlap with next program's focus
        relevantTools = this.filterToolsForSequence(relevantTools, program, nextProgram);
        console.log(`🔄 Filtered tools for ${program.program_of_work} considering next program ${nextProgram.program_of_work}: ${relevantTools.length} tools remaining`);
      }

      // Calculate tools by stages
      const stages = this.calculateProgramLengthAndStages(relevantTools);
      program.toolsByStage = stages;

        // Calculate total program length
        program.totalProgramLength = Object.values(stages).reduce(
          (total, stage) => total + stage.totalDuration,
          0,
        );

        // Filter relevant deliverables
        const relevantDeliverables = this.filterRelevantDeliverables(
          deliverables,
          designChallenge,
          program.program_of_work,
        );

        program.deliverables = relevantDeliverables;

        // Add additional required fields
        program.goal =
          program.outcome || `Define goals for ${program.program_of_work}`;
        program.expectedDeliverables = relevantDeliverables.map(
          (d) => d.deliverable,
        ) || [`Deliverables for ${program.program_of_work}`];
        program.stakeholdersInvolvement = [
          "Client stakeholders",
          "Design team",
        ];
        program.isIncluded = true; // Ensure all programs are enabled by default
      });

      // Create a simple version for display
      const simplifiedPrograms = finalProgramSequence.map((program, index) => ({
        id: index + 1,
        name: program.program_of_work,
        phase: program.phase,
        duration: program.totalProgramLength || 40,
        goal: program.goal,
        methods: "Various methods...",
        tools: Object.keys(program.toolsByStage || {}).length,
      }));

      console.log(`📋 Final program sequence created:`, {
        count: finalProgramSequence.length,
        totalDuration: finalProgramSequence.reduce(
          (total, p) => total + (p.totalProgramLength || 40),
          0,
        ),
        programs: simplifiedPrograms,
      });

      console.log("✅ Programs step completed with sophisticated scoring:", {
        selectedProgramsCount: selectedPrograms.length,
        finalSequenceCount: finalProgramSequence.length,
        totalDuration: finalProgramSequence.reduce(
          (total, p) => total + (p.totalProgramLength || 40),
          0,
        ),
      });

      this.workflowResults.programs = {
        selectedPrograms,
        finalProgramSequence,
        similarityScores: similarityData,
        totalDuration: finalProgramSequence.reduce(
          (total, p) => total + (p.totalProgramLength || 40),
          0,
        ),
        lemmatizedChallenge: similarityData.lemmatized_challenge,
      };
    } catch (error) {
      console.error("Programs step failed:", error);
      throw error;
    }
  }

  // Filter tools to prevent overlap between sequential programs
  filterToolsForSequence(tools, currentProgram, nextProgram) {
    if (!nextProgram) return tools;

    const currentPhase = currentProgram.phase?.toLowerCase() || '';
    const nextPhase = nextProgram.phase?.toLowerCase() || '';
    const nextProgramName = nextProgram.program_of_work?.toLowerCase() || '';

    return tools.filter(tool => {
      const toolName = tool.tool?.toLowerCase() || '';
      const toolDescription = tool.description?.toLowerCase() || '';

      // Skip testing tools if next program is validation/testing focused
      if ((nextPhase.includes('validation') || nextPhase.includes('testing') || nextProgramName.includes('test') || nextProgramName.includes('validation')) &&
          (toolName.includes('test') || toolName.includes('validation') || toolDescription.includes('usability test') || toolDescription.includes('user test'))) {
        console.log(`⚠️ Skipping tool "${tool.tool}" - next program will handle testing`);
        return false;
      }

      // Skip implementation tools if next program is implementation focused  
      if ((nextPhase.includes('implementation') || nextProgramName.includes('implementation')) &&
          (toolName.includes('implement') || toolName.includes('deploy') || toolDescription.includes('implementation'))) {
        console.log(`⚠️ Skipping tool "${tool.tool}" - next program will handle implementation`);
        return false;
      }

      // Skip research tools if next program is research/discovery focused
      if ((nextPhase.includes('discovery') || nextPhase.includes('research') || nextProgramName.includes('research') || nextProgramName.includes('discovery')) &&
          (toolName.includes('research') || toolName.includes('interview') || toolDescription.includes('user research'))) {
        console.log(`⚠️ Skipping tool "${tool.tool}" - next program will handle research`);
        return false;
      }

      return true;
    });
  }

  async timelineStep() {
    this.workflowResults.timeline = {
      totalDuration: this.workflowResults.programs?.totalDuration || "12 weeks",
      phases: [],
    };
  }

  async finalizeStep() {
    console.log(
      "🎯 Starting finalization step with comprehensive data processing...",
    );

    // Add timeout to prevent hanging
    const finalizationTimeout = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error("Finalization step timeout after 15 seconds")),
        15000, // Increased timeout
      );
    });

    const finalizationWork = new Promise(async (resolve) => {
      try {
        // Ensure all workflow results are properly compiled
        const compiledResults = this.compileWorkflowResults();

        // CRITICAL: Validate no exponential duplication before processing
        if (
          compiledResults.finalProgramSequence &&
          compiledResults.finalProgramSequence.length > 15
        ) {
          console.error(
            "❌ WORKFLOW CORRUPTION: Too many programs generated:",
            compiledResults.finalProgramSequence.length,
          );
          throw new Error(
            `Workflow generated ${compiledResults.finalProgramSequence.length} programs. Maximum 15 allowed. This indicates a data corruption bug.`,
          );
        }

        // Check for duplicate program names (exponential duplication indicator)
        const programNames = compiledResults.finalProgramSequence.map(
          (p) => p.program_of_work || p.name || "unnamed",
        );
        const uniqueNames = new Set(programNames);
        if (programNames.length > uniqueNames.size * 2) {
          console.error(
            "❌ WORKFLOW DUPLICATION: Detected duplicate programs:",
            {
              total: programNames.length,
              unique: uniqueNames.size,
              names: programNames.slice(0, 5),
            },
          );
          throw new Error(
            "Workflow generated duplicate programs. This indicates a corruption bug.",
          );
        }

        // Enhanced results with metadata

        // Enhanced validation with detailed logging
        console.log("🔍 Finalization validation check:", {
          hasPrograms: !!compiledResults.finalProgramSequence,
          programsCount: compiledResults.finalProgramSequence?.length || 0,
          hasShortSummary: !!compiledResults.shortSummary,
          hasSelectedPrograms: !!compiledResults.selectedPrograms,
          selectedProgramsCount: compiledResults.selectedPrograms?.length || 0,
          totalDuration: compiledResults.totalDuration,
        });

        // Validate that we have essential data before proceeding
        if (
          !compiledResults.finalProgramSequence ||
          compiledResults.finalProgramSequence.length === 0
        ) {
          console.error(
            "❌ Finalization failed: No programs in final sequence",
          );
          console.error("🔍 Available workflow results:", {
            analyze: !!this.workflowResults.analyze,
            objectives: !!this.workflowResults.objectives,
            programs: !!this.workflowResults.programs,
            programsData: this.workflowResults.programs
              ? {
                  selectedPrograms:
                    this.workflowResults.programs.selectedPrograms?.length,
                  finalProgramSequence:
                    this.workflowResults.programs.finalProgramSequence?.length,
                }
              : "No programs data",
          });
          throw new Error("Cannot finalize strategy without programs");
        }

        // Additional validation for program structure
        const validPrograms = compiledResults.finalProgramSequence.filter(
          (program) => {
            return (
              program &&
              typeof program === "object" &&
              (program.program_of_work || program.name || program.title)
            );
          },
        );

        if (validPrograms.length === 0) {
          console.error("❌ Finalization failed: No valid programs found");
          throw new Error("Cannot finalize strategy without valid programs");
        }

        console.log("✅ Finalization validation passed:", {
          programsCount: validPrograms.length,
          totalDuration: compiledResults.totalDuration,
          hasShortSummary: !!compiledResults.shortSummary,
          programNames: validPrograms
            .map((p) => p.program_of_work || p.name || p.title)
            .slice(0, 3),
        });

        // Store compiled results with all data ready for immediate save
        this.workflowResults.complete = true;
        this.workflowResults.timestamp = new Date().toISOString();
        this.workflowResults.validated = true;
        this.workflowResults.finalCompiledResults = compiledResults;

        // Calculate proper total duration for save
        const calculatedTotalDuration = validPrograms.reduce((sum, program) => {
          return sum + (program.totalProgramLength || program.duration || 40); // Default to 40 hours
        }, 0);

        // Prepare complete data for single save operation with proper formatting
        this.workflowResults.readyForSave = {
          shortSummary: compiledResults.shortSummary,
          problemStatement: compiledResults.problemStatement,
          goalStatement: compiledResults.goalStatement,
          targetStatement: compiledResults.targetStatement,
          objectives: compiledResults.objectives, // Keep as array for proper handling
          deliverables: compiledResults.deliverables, // Keep as array for proper handling
          metrics: compiledResults.metrics, // Keep as array for proper handling
          experienceDesignPrinciples:
            compiledResults.experienceDesignPrinciples, // CRITICAL FIX: Include in save data
          selectedPrograms: validPrograms, // Use validated programs
          finalProgramSequence: validPrograms, // Use validated programs
          stakeholders: Array.isArray(compiledResults.stakeholders)
            ? compiledResults.stakeholders.join("\n")
            : compiledResults.stakeholders,
          totalDuration: calculatedTotalDuration, // Use calculated value
          conversationHistory: this.conversationHistory || [], // Include conversation history
        };

        // IMPORTANT: Also store conversation history in compiled results for access
        compiledResults.conversationHistory = this.conversationHistory || [];

        console.log("💬 Conversation history included in results:", {
          messagesCount: compiledResults.conversationHistory.length,
          hasMessages: compiledResults.conversationHistory.length > 0,
        });

        // CRITICAL: Store final compiled results directly in workflow results to prevent loss
        this.workflowResults.selectedPrograms = validPrograms;
        this.workflowResults.finalProgramSequence = validPrograms;

        console.log(
          "🎉 Strategy finalization complete - all data ready for single save operation:",
          {
            validProgramsCount: validPrograms.length,
            calculatedDuration: calculatedTotalDuration,
            readyForSaveSize: JSON.stringify(this.workflowResults.readyForSave)
              .length,
          },
        );

        resolve();
      } catch (error) {
        console.error("❌ Finalization error:", error);
        throw error;
      }
    });

    // Race between finalization work and timeout
    await Promise.race([finalizationWork, finalizationTimeout]);
  }

  compileWorkflowResults() {
    const results = {};

    console.log("StrategyWorkflowService: Compiling workflow results");
    console.log("Available workflow results:", {
      hasAnalyze: !!this.workflowResults.analyze,
      hasObjectives: !!this.workflowResults.objectives,
      hasPrograms: !!this.workflowResults.programs,
      hasTimeline: !!this.workflowResults.timeline,
      programsData: this.workflowResults.programs
        ? {
            selectedPrograms:
              this.workflowResults.programs.selectedPrograms?.length || 0,
            finalProgramSequence:
              this.workflowResults.programs.finalProgramSequence?.length || 0,
            totalDuration: this.workflowResults.programs.totalDuration,
          }
        : "No programs data",
    });

    // Extract data from analyze step
    if (this.workflowResults.analyze) {
      results.shortSummary = this.workflowResults.analyze.short_summary;
      results.problemStatement = this.workflowResults.analyze.problem_statement;
      results.goalStatement = this.workflowResults.analyze.goal_statement;
      results.targetStatement = this.workflowResults.analyze.target_statement;

      results.objectives = Array.isArray(
        this.workflowResults.analyze.objectives_statement,
      )
        ? this.workflowResults.analyze.objectives_statement
        : [this.workflowResults.analyze.objectives_statement || ""];

      results.deliverables = Array.isArray(
        this.workflowResults.analyze.deliverables_statement,
      )
        ? this.workflowResults.analyze.deliverables_statement
        : [this.workflowResults.analyze.deliverables_statement || ""];

      results.metrics = Array.isArray(
        this.workflowResults.analyze.metrics_statement,
      )
        ? this.workflowResults.analyze.metrics_statement
        : [this.workflowResults.analyze.metrics_statement || ""];

      // CRITICAL FIX: Include experienceDesignPrinciples in compiled results
      results.experienceDesignPrinciples =
        this.workflowResults.analyze.experience_design_principles;
    }

    // Extract programs data
    if (this.workflowResults.programs) {
      results.selectedPrograms =
        this.workflowResults.programs.selectedPrograms || [];
      results.finalProgramSequence =
        this.workflowResults.programs.finalProgramSequence || [];
      results.totalDuration = this.workflowResults.programs.totalDuration || 0;

      console.log("✅ Extracted programs data:", {
        selectedPrograms: results.selectedPrograms.length,
        finalProgramSequence: results.finalProgramSequence.length,
        totalDuration: results.totalDuration,
        programNames: results.finalProgramSequence.map(
          (p) => p.program_of_work,
        ),
        firstProgram: results.finalProgramSequence[0]
          ? {
              name: results.finalProgramSequence[0].program_of_work,
              phase: results.finalProgramSequence[0].phase,
              duration: results.finalProgramSequence[0].totalProgramLength,
              toolsCount: Object.keys(
                results.finalProgramSequence[0].toolsByStage || {},
              ).length,
            }
          : "No first program",
      });
    } else {
      console.log("❌ No programs data found in workflow results!");
      results.selectedPrograms = [];
      results.finalProgramSequence = [];
      results.totalDuration = 0;
    }

    results.stakeholders = [
      "Client stakeholder involvement will be determined during program execution.",
    ];

    console.log("📤 Final compiled results:", {
      hasShortSummary: !!results.shortSummary,
      hasPrograms: results.finalProgramSequence?.length > 0,
      programCount: results.finalProgramSequence?.length || 0,
      totalDuration: results.totalDuration,
      hasObjectives: results.objectives?.length > 0,
      hasDeliverables: results.deliverables?.length > 0,
      resultKeys: Object.keys(results),
    });

    return results;
  }

  async loadAgentProfiles() {
    if (!this.agentProfiles) {
      try {
        const response = await fetchWithAuth("/agents/capabilities");

        if (response.ok) {
          const data = await response.json();

          console.log("🔍 Raw agent data from API:", {
            hasAgents: !!data.agents,
            agentsType: typeof data.agents,
            agentsKeys: data.agents ? Object.keys(data.agents) : [],
            sampleAgent: data.agents ? Object.values(data.agents)[0] : null,
          });

          // The API returns agents as an object, not an array
          if (data.agents && typeof data.agents === "object") {
            this.agentProfiles = {};
            // Convert the agents object to the format we expect
            Object.entries(data.agents).forEach(([stringKey, agent]) => {
              if (agent && agent.id) {
                this.agentProfiles[agent.id] = {
                  ...agent,
                  stringKey: stringKey,
                };
              }
            });
          } else {
            console.error("❌ Unexpected agent data structure:", data);
            this.agentProfiles = {};
          }

          console.log("🔍 Processed agent profiles:", {
            profilesCount: Object.keys(this.agentProfiles).length,
            profilesKeys: Object.keys(this.agentProfiles),
            sampleProfile: Object.values(this.agentProfiles)[0],
          });
        } else {
          console.warn(
            `⚠️ Agent capabilities API returned ${response.status}, using fallback`,
          );
          this.agentProfiles = {};
        }
      } catch (error) {
        console.error("❌ Failed to load agent profiles:", error);
        this.agentProfiles = {};
      }
    }

    // Always ensure we have at least a basic agent profile structure
    if (!this.agentProfiles || Object.keys(this.agentProfiles).length === 0) {
      console.log("🔧 Creating fallback agent profiles");
      this.agentProfiles = {
        1: {
          id: 1,
          name: "Product Lead",
          role: "strategic_planning",
          enabled: true,
          primary_ai_capabilities: ["analyze_brief", "generate_goal"],
          stringKey: "DESIGN_LEAD",
        },
        2: {
          id: 2,
          name: "UX Research Lead",
          role: "research_planning",
          enabled: true,
          primary_ai_capabilities: ["compute_similarity"],
          stringKey: "UX_RESEARCH",
        },
      };
    }

    return this.agentProfiles;
  }

  getAgentForStep(stepId) {
    const step = this.STRATEGY_STEPS.find((s) => s.id === stepId);
    if (!step || !this.agentProfiles) {
      console.log("🔍 No step or agent profiles for stepId:", stepId);
      return null;
    }

    // Find agent by key from agents.js - try multiple approaches
    const agentKey = step.agentKey;

    // First try: direct lookup by numeric ID
    const directAgent = Object.values(this.agentProfiles).find(
      (agent) =>
        agent.stringKey === agentKey ||
        agent.role?.toUpperCase().includes(agentKey.split("_")[0]),
    );

    if (directAgent) {
      console.log(`✅ Found agent for step ${stepId}:`, directAgent.name);
      return directAgent;
    }

    // Fallback: return first available agent
    const fallbackAgent = Object.values(this.agentProfiles)[0];
    if (fallbackAgent) {
      console.log(
        `⚠️ Using fallback agent for step ${stepId}:`,
        fallbackAgent.name,
      );
      return fallbackAgent;
    }

    console.log(`❌ No agent found for step ${stepId} with key ${agentKey}`);
    return null;
  }

  getAgentStringId(numericId) {
    if (!this.agentProfiles) return null;

    // Find the string key for the agent with this numeric ID
    const agentEntry = Object.entries(this.agentProfiles).find(
      ([stringId, agent]) => agent.id === numericId,
    );

    return agentEntry ? agentEntry[0] : null;
  }

  reset() {
    this.currentStep = 0;
    this.stepData = {};
    this.isCompleted = false;
    this.conversationHistory = [];
    this.agentProfiles = null; // Reset agent profiles
  }

  // Add conversation message to history
  addConversationMessage(message) {
    this.conversationHistory.push({
      ...message,
      timestamp: new Date().toISOString(),
    });
  }

  // Dynamic agent tool execution
  async executeAgentTool(agent, toolName, params) {
    try {
      // Get tool definition from AI_REASONING_TOOLS
      const toolInfo = await this.getToolInfo(toolName);
      if (!toolInfo) {
        throw new Error(`Tool ${toolName} not found in registry`);
      }

      console.log(`🔧 Executing ${toolName} via agent ${agent.name}:`, {
        endpoint: toolInfo.endpoint,
        method: toolInfo.method,
        hasRequiredParams: toolInfo.requiredParams?.every(
          (param) => params[param],
        ),
      });

      const response = await fetch(`/api${toolInfo.endpoint}`, {
        method: toolInfo.method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...params,
          agentId: this.getAgentStringId(agent.id),
          currentAgent: agent,
          toolName: toolName,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `${toolName} execution failed with status ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error(`Failed to execute ${toolName}:`, error);
      throw error;
    }
  }

  // Fallback for direct API calls when agent system fails
  async executeDirectToolCall(endpoint, params) {
    console.log("🔧 Using direct API call:", endpoint);

    try {
      // Add timeout to prevent hanging - 5 minutes for long-running AI operations
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minute timeout for AI operations

      const response = await fetchWithAuth(endpoint, {
        method: "POST",
        body: params,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(
          `API call failed with status ${response.status}: ${response.statusText}`,
        );
      }

      const result = await response.json();
      console.log("✅ Direct API call successful");
      return result;
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("API call timed out after 5 minutes");
      }
      console.error("❌ Direct API call failed:", error);
      throw error;
    }
  }

  // Get tool information from agents system
  async getToolInfo(toolName) {
    try {
      // This should fetch from the AI_REASONING_TOOLS registry
      // For now, hardcode the known tools, but this should be dynamic
      const toolRegistry = {
        analyze_brief: {
          endpoint: "/autonomous-ai/autonomous-analysis",
          method: "POST",
          requiredParams: ["design_challenge"],
        },
        generate_goal: {
          endpoint: "/ai/generate-goal",
          method: "POST",
          requiredParams: ["design_challenge"],
        },
        compute_similarity: {
          endpoint: "/ai/compute-similarity-scores",
          method: "POST",
          requiredParams: ["design_challenge"],
        },
      };

      return toolRegistry[toolName] || null;
    } catch (error) {
      console.error("Failed to get tool info:", error);
      return null;
    }
  }

  // Helper method to parse agent analysis responses
  parseAgentAnalysisResponse(botMessage) {
    try {
      // Try to extract JSON from agent response
      const jsonMatch = botMessage.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[1]);
      }

      // Fallback: create structured response from text
      return {
        short_summary: "AI-generated project summary",
        problem_statement: botMessage.substring(0, 200),
        goal_statement: "Define strategic goals based on analysis",
        target_statement: "Target audience",
        objectives_statement: [
          "Strategic objective 1",
          "Strategic objective 2",
        ],
        deliverables_statement: ["Key deliverable 1", "Key deliverable 2"],
        metrics_statement: ["Success metric 1", "Success metric 2"],
      };
    } catch (error) {
      console.warn("Failed to parse agent response:", error);
      return {
        short_summary: "Analysis completed",
        problem_statement: botMessage,
        goal_statement: "Strategic goals to be defined",
        target_statement: "Target users",
        objectives_statement: ["Define objectives"],
        deliverables_statement: ["Define deliverables"],
        metrics_statement: ["Define metrics"],
      };
    }
  }
}

export default StrategyWorkflowService;